import type { SearchProvider, SearchResult } from "../types";
import type ApiService from "../../ApiService";
import { softContains } from "../../../utils/stringUtil";
import { IconTile } from "../../../components/icon/IconTile";
import { Show } from "solid-js";
import Text from "../../../components/text/Text";
import { isNil } from "lodash-es";
import { takeWhere } from "../searchUtil.ts";

export class FolderSearchProvider implements SearchProvider {
  readonly type = "FOLDER";

  constructor(
    private readonly foldersQuery: ReturnType<typeof ApiService.getFolders>,
    private readonly maxResults: number,
  ) {}

  async search(query: string): Promise<SearchResult[]> {
    if (isNil(this.foldersQuery.data)) {
      return [];
    }

    return Array.from(
      takeWhere(
        this.foldersQuery.data,
        (folder) =>
          folder.id.equals(query) ||
          softContains(folder.meta.name, query) ||
          softContains(folder.meta.description ?? "", query),
        this.maxResults,
      ).map((folder) => ({
        type: this.type,
        id: folder.id.toString(),
        ContentComponent: () => (
          <div
            class="grid min-w-0 grid-cols-template gap-3 px-3 py-2.5"
            style={{
              "--column-template": "auto minmax(0, 1fr)",
            }}
          >
            <div class="pt-1">
              <IconTile icon={folder.meta.icon} size="sm" color="icon-tertiary" border />
            </div>
            <div class="flex flex-col justify-center gap-0.5">
              <Text class="text-base-medium text-primary" as="p" text={folder.meta.name} truncate />
              <Show when={folder.meta.description}>
                {(description) => (
                  <Text class="text-small-regular text-secondary" as="p" text={description()} truncate />
                )}
              </Show>
            </div>
          </div>
        ),
        actions: {
          Enter: {
            type: "navigate",
            href: (customerId) => `/customers/${customerId}/folders/${folder.id}`,
          },
        },
      })),
    );
  }
}
